/**
 * SocialEnquiry Page Component
 * Simplified suite enquiry form — no payment collection, no cost calculator.
 * Same layout/branding as the former SocialBooking page, submits to the same
 * public /bookings/event-request endpoint, tagged booking_type: 'suite-enquiry'
 * so it can be distinguished from full room-hire bookings and quick
 * pre-enquiries on the admin dashboard.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Social.css';
import { fetchWithBackendFallback } from '../../utils/backendApi';
import { getSiteContent } from '../../utils/siteContent';
import { SUITES } from './suiteOptions';

const EVENT_TYPE_OPTIONS = [
  'Private',
  'Public',
  'Live Music Sessions',
  'Family Events & Discos',
  'Private Hire & Celebrations',
  'Community Nights',
  'Weddings (after-party)',
  'Funeral (wake, celebration of life)',
  'Other',
];

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  suiteId: SUITES[0]?.id || '',
  wantsDj: false,
  eventTypeChoice: 'Private',
  eventTypeOther: '',
};

const SocialEnquiry = () => {
  const [siteContent, setSiteContent] = useState(() => getSiteContent());
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const handleContentUpdate = () => setSiteContent(getSiteContent());
    window.addEventListener('site-content-updated', handleContentUpdate);
    return () => window.removeEventListener('site-content-updated', handleContentUpdate);
  }, []);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const trimmedFirstName = form.firstName.trim();
    const trimmedLastName = form.lastName.trim();
    const trimmedEmail = form.email.trim();
    const trimmedPhone = form.phone.trim();

    if (!trimmedFirstName || !trimmedLastName) {
      setError('Please provide your first and last name.');
      return;
    }

    if (!trimmedEmail) {
      setError('Please provide your email.');
      return;
    }

    const resolvedEventType = form.eventTypeChoice === 'Other'
      ? form.eventTypeOther.trim()
      : form.eventTypeChoice;

    if (!resolvedEventType) {
      setError('Please enter what type of event this enquiry is for.');
      return;
    }

    if (!form.suiteId) {
      setError('Please select which suite you are enquiring about.');
      return;
    }

    const selectedSuite = SUITES.find((suite) => suite.id === form.suiteId);
    const suiteName = selectedSuite?.name || form.suiteId;

    const notes = [
      `Enquired suite: ${suiteName}`,
      `Event type: ${resolvedEventType}`,
      `DJ interest: ${form.wantsDj ? 'Yes' : 'No'}`,
    ].join('\n');

    setSubmitting(true);
    try {
      await fetchWithBackendFallback('/bookings/event-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_title: `${suiteName} enquiry`,
          group_name: 'Clipstone Social Club',
          full_name: `${trimmedFirstName} ${trimmedLastName}`,
          email: trimmedEmail,
          phone: trimmedPhone || null,
          booking_section: 'social',
          booking_type: 'suite-enquiry',
          notes,
        }),
      });

      setSuccess('Thanks — your enquiry has been sent. We\'ll be in touch by email.');
      window.dispatchEvent(new Event('booking-request-updated'));
      setForm(initialForm);
    } catch (err) {
      setError(err.message || 'Unable to send your enquiry right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="social-page social-page--subpage">
      <section id="social-enquiry-hero" className="social-subpage-hero">
        <p className="social-kicker">Social Club</p>
        <h1>{siteContent.forms.socialSuiteEnquiryHeroTitle}</h1>
        <p>
          {siteContent.forms.socialSuiteEnquiryHeroSubtitle}
        </p>
        <div className="social-subpage-actions">
          <Link to="/social" className="social-subpage-link">Back to Social</Link>
          <Link to="/social/staff" className="social-subpage-link social-subpage-link--filled">View Staff Page</Link>
        </div>
      </section>

      <section className="social-content">
        <div id="social-enquiry-pricing-grid" className="social-pricing-grid">
          {SUITES.map((suite) => (
            <article key={suite.name} className="social-card social-pricing-card">
              <h3>{suite.name}</h3>
              <p className="social-pricing-capacity">Capacity: {suite.capacity}</p>

              <ul className="social-pricing-list" aria-label={`${suite.name} pricing`}>
                {suite.costs.map((cost) => (
                  <li key={`${suite.name}-${cost.item}`}>
                    <div>
                      <strong>{cost.item}</strong>
                      {cost.note && <p>{cost.note}</p>}
                    </div>
                    <span>{cost.price}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div id="social-enquiry-form-shell" className="social-booking-shell social-rental-shell">
          <div className="social-section-heading">
            <p>{siteContent.forms.socialSuiteEnquirySectionLabel}</p>
            <h2>{siteContent.forms.socialSuiteEnquirySectionTitle}</h2>
          </div>

          <form className="social-booking-form" onSubmit={handleSubmit}>
            <div className="social-booking-grid">
              <label>
                <span>First name *</span>
                <input required value={form.firstName} onChange={(e) => updateForm('firstName', e.target.value)} />
              </label>
              <label>
                <span>Last name *</span>
                <input required value={form.lastName} onChange={(e) => updateForm('lastName', e.target.value)} />
              </label>
              <label>
                <span>Email *</span>
                <input required type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} />
              </label>
              <label>
                <span>Phone</span>
                <input value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
              </label>
              <label>
                <span>Suite</span>
                <select value={form.suiteId} onChange={(e) => updateForm('suiteId', e.target.value)}>
                  {SUITES.map((suite) => (
                    <option key={suite.id} value={suite.id}>{suite.name}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Event type</span>
                <select value={form.eventTypeChoice} onChange={(e) => updateForm('eventTypeChoice', e.target.value)}>
                  {EVENT_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
              {form.eventTypeChoice === 'Other' && (
                <label>
                  <span>Other event type *</span>
                  <input
                    required
                    value={form.eventTypeOther}
                    onChange={(e) => updateForm('eventTypeOther', e.target.value)}
                    placeholder="Type your event type"
                  />
                </label>
              )}
            </div>

            <label className="social-rental-checkbox">
              <input type="checkbox" checked={form.wantsDj} onChange={(e) => updateForm('wantsDj', e.target.checked)} />
              <span>Interested in a DJ</span>
            </label>

            {error && <div className="social-booking-alert social-booking-alert--error">{error}</div>}
            {success && <div className="social-booking-alert social-booking-alert--success">{success}</div>}

            <button type="submit" className="social-booking-submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Send enquiry'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default SocialEnquiry; 